import type { ReactNode } from 'react'

import Link from 'next/link'
import { motion } from 'framer-motion'
import {
    ArrowRight,
    Bot,
    Check,
    Flame,
    MonitorCheck
} from 'lucide-react'
import Logo from '@/components/generals/logo'
import { EASE_OUT } from '@/components/generals/motion'

const PERKS = [
    "دوره‌ها را در وب کشف و به کتابخانه‌تان اضافه کنید",
    "با اسلایدهای زنده و مربی هوش مصنوعی در اپلیکیشن دسکتاپ بیاموزید",
    "پیشرفت، اشتراک و دوره‌های ساخت خودتان را در داشبورد ببینید"
]

const AuthVisual = (): ReactNode => {
    return (
        <aside className='relative isolate hidden overflow-hidden rounded-[2rem] bg-night p-10 lg:flex lg:flex-col lg:justify-between' aria-hidden='true'>
            <div className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -top-32 -left-24 size-[28rem] rounded-full bg-primary/70 blur-3xl' />
                <div className='absolute -right-24 -bottom-32 size-[26rem] rounded-full bg-[#5F52D9]/40 blur-3xl motion-safe:animate-drift' />
                <div className='absolute inset-0 opacity-60 fade-radial [background-image:linear-gradient(to_right,rgb(255_255_255/0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgb(255_255_255/0.05)_1px,transparent_1px)] [background-size:44px_44px]' />
            </div>

            <span className='inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-bold text-white/85 ring-1 ring-white/15'>
                یک حساب، همه مسیر یادگیری
            </span>

            <div className='relative mx-auto my-10 w-full max-w-sm'>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.1 }}
                    className='rounded-3xl bg-white p-3 shadow-[0_40px_80px_-30px_rgb(0_0_0/0.6)]'
                >
                    <div className='relative h-36 overflow-hidden rounded-2xl bg-gradient-to-br from-[#2E2A7A] via-[#5B54D6] to-[#A9A4F2]'>
                        <div className='absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_1px_1px,rgb(255_255_255/0.8)_1px,transparent_0)] [background-size:16px_16px]' />
                        <span className='absolute top-3 right-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold text-foreground'>هوش مصنوعی</span>
                    </div>
                    <div className='p-3'>
                        <p className='text-base font-bold text-foreground'>مبانی یادگیری ماشین</p>
                        <div className='mt-3 flex items-center gap-2'>
                            <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-primary-soft'>
                                <motion.div
                                    className='h-full rounded-full bg-primary'
                                    initial={{ width: 0 }}
                                    animate={{ width: '72%' }}
                                    transition={{ duration: 1.4, ease: EASE_OUT, delay: 0.6 }}
                                />
                            </div>
                            <span className='text-xs font-bold text-foreground'>۷۲٪</span>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.5 }}
                    className='absolute -bottom-10 -right-10 w-64'
                >
                    <div className='animate-float rounded-2xl bg-white/95 p-3 shadow-lifted backdrop-blur'>
                        <div className='flex items-center gap-2'>
                            <span className='grid size-8 place-items-center rounded-xl bg-gradient-to-br from-primary to-[#6B5BD8] text-white'>
                                <Bot className='size-4' />
                            </span>
                            <span className='text-xs font-bold text-foreground'>مربی هوشمند</span>
                        </div>
                        <p className='mt-2 rounded-xl rounded-br-md bg-secondary px-3 py-2 text-xs leading-6 text-secondary-foreground'>
                            خوش برگشتید! آماده‌اید درس امروز را از همان‌جا که ماندید ادامه دهیم؟
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.8 }}
                    className='absolute -top-8 -left-8'
                >
                    <div className='animate-float-late flex items-center gap-2 rounded-2xl bg-white/95 py-2 ps-2 pe-3 shadow-lifted'>
                        <span className='grid size-8 place-items-center rounded-xl bg-success/10 text-success'>
                            <MonitorCheck className='size-4' />
                        </span>
                        <span className='text-xs font-bold text-foreground'>همگام با اپلیکیشن دسکتاپ</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: EASE_OUT, delay: 1.1 }}
                    className='absolute top-1/2 -left-12 inline-flex items-center gap-1.5 rounded-full bg-white/95 py-1.5 ps-1.5 pe-3 text-xs font-bold text-foreground shadow-lifted'
                >
                    <span className='grid size-6 place-items-center rounded-full bg-warning/15 text-warning'>
                        <Flame className='size-3.5' />
                    </span>
                    ۷ روز پیاپی
                </motion.div>
            </div>

            <div>
                <p className='text-2xl font-extrabold leading-[1.5] text-white'>
                    یادگیری وقتی عمیق می‌شود که بتوانید هر لحظه بپرسید، امتحان کنید و پیشرفتتان را ببینید.
                </p>
                <ul className='mt-6 flex flex-col gap-3'>
                    {PERKS.map((perk) => (
                        <li key={perk} className='flex items-start gap-2.5 text-sm text-white/75'>
                            <span className='mt-1 grid size-5 shrink-0 place-items-center rounded-full bg-white/15 text-white'>
                                <Check className='size-3' />
                            </span>
                            {perk}
                        </li>
                    ))}
                </ul>
            </div>
        </aside>
    )
}

interface AuthShellProps {
    title: string
    description: ReactNode
    children: ReactNode
    footer?: ReactNode
}

const AuthShell = ({ title, description, children, footer }: AuthShellProps): ReactNode => {
    return (
        <div className='min-h-screen p-3 sm:p-4'>
            <div className='mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-6xl gap-4 sm:min-h-[calc(100vh-2rem)] lg:grid-cols-[1fr_1.05fr]'>
                <main id='main' className='relative isolate flex flex-col rounded-[2rem] bg-white/60 px-5 py-6 shadow-soft ring-1 ring-white backdrop-blur sm:px-10'>
                    <div className='pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[2rem]' aria-hidden='true'>
                        <div className='absolute -top-32 -right-20 size-80 rounded-full bg-lavender/50 blur-3xl' />
                    </div>

                    <div className='flex items-center justify-between gap-3'>
                        <Logo />
                        <Link
                            href='/'
                            className='inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition hover:bg-white hover:text-foreground'
                        >
                            <ArrowRight className='size-4' aria-hidden='true' />
                            بازگشت به سایت
                        </Link>
                    </div>

                    <div className='mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10'>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE_OUT }}
                        >
                            <h1 className='text-3xl font-extrabold text-foreground'>
                                {title}
                            </h1>
                            <div className='mt-2 text-sm text-muted-foreground'>
                                {description}
                            </div>

                            <div className='mt-8'>
                                {children}
                            </div>

                            {footer && (
                                <div className='mt-8 text-center text-sm text-muted-foreground'>
                                    {footer}
                                </div>
                            )}
                        </motion.div>
                    </div>

                    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
                        <Link href='/terms' className='hover:text-primary'>قوانین و شرایط</Link>
                        <Link href='/privacy' className='hover:text-primary'>حریم خصوصی</Link>
                        <Link href='/contact' className='hover:text-primary'>تماس با ما</Link>
                    </div>
                </main>

                <AuthVisual />
            </div>
        </div>
    )
}

export default AuthShell
